import React, { useState, useEffect } from 'react';
import { X, Copy, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';

interface FullScreenTextModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  onCopy?: () => void;
  editable?: boolean;
  onSave?: (newContent: string) => void;
}

export const FullScreenTextModal: React.FC<FullScreenTextModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
  onCopy,
  editable = false,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(editable);
  const [editedContent, setEditedContent] = useState(content);

  // Update editing state when editable prop changes
  useEffect(() => {
    if (isOpen) {
      setIsEditing(editable);
      setEditedContent(content);
    }
  }, [isOpen, editable, content]);

  // Handle Escape key to close modal and auto-save if editing
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing && onSave) {
          onSave(editedContent);
        }
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isEditing, onClose, editedContent, onSave]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isEditing && onSave) {
      onSave(editedContent);
    }
    onClose();
  };

  return (
    <div
      data-testid="fullscreen-modal"
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center pt-16"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-card rounded-lg w-full h-full max-w-7xl max-h-[calc(95vh-4rem)] flex flex-col m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-5 h-5 text-muted-foreground" />
            <h2 data-testid="modal-title" className="text-xl font-semibold">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && onCopy && (
              <Button data-testid="modal-copy-btn" variant="outline" size="sm" onClick={onCopy}>
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            )}
            <Button data-testid="modal-close-btn" variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isEditing ? (
            <textarea
              data-testid="modal-textarea"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-full text-sm font-mono whitespace-pre-wrap break-words bg-background border rounded p-4 resize-none"
            />
          ) : (
            <pre data-testid="modal-content" className="text-sm font-mono whitespace-pre-wrap break-words">
              {content}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
